import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
type SubmitStatus = 'idle' | 'sending' | 'success' | 'error';
const ACCESS_KEY = '5acab686-1f1e-463a-8647-98a6caca9d66';
const SUBJECT = 'Nueva solicitud de asesoría - Landing G&M';
export function LeadCaptureForm() {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    ciudad: '',
    whatsapp: ''
  });
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [feedback, setFeedback] = useState('');
  const cities = ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga', 'Manizales', 'Pereira', 'Santa Marta', 'Otra'];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (status === 'error' || status === 'success') {
      setStatus('idle');
      setFeedback('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const {
      nombre,
      empresa,
      ciudad,
      whatsapp
    } = formData;
    if (!nombre.trim() || !empresa.trim() || !ciudad || !whatsapp.trim()) {
      setStatus('error');
      setFeedback('Por favor complete todos los campos requeridos.');
      return;
    }
    setStatus('sending');
    setFeedback('');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: ACCESS_KEY,
          subject: SUBJECT,
          'Nombre Completo': nombre,
          'Empresa / Proyecto': empresa,
          Ciudad: ciudad,
          WhatsApp: whatsapp
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setStatus('success');
        setFeedback('¡Gracias! Nos pondremos en contacto pronto.');
        setFormData({
          nombre: '',
          empresa: '',
          ciudad: '',
          whatsapp: ''
        });
      } else {
        setStatus('error');
        setFeedback('No pudimos enviar su solicitud. Intente de nuevo o escríbanos por WhatsApp.');
      }
    } catch {
      setStatus('error');
      setFeedback('Ocurrió un problema de conexión. Verifique su internet e intente de nuevo.');
    }
  };
  const isSending = status === 'sending';
  return <section id="contacto" className="py-24 bg-bone border-t border-gray-200" data-id="element-104" data-mp="gqf7632tiap">
      <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-105" data-mp="enffuc4gass">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white p-10 md:p-16 shadow-2xl rounded-sm border border-gray-100 relative overflow-hidden" data-id="element-106" data-mp="3rmi2lxpgygtt">
          {/* Decorative accent */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gold" data-id="element-107" data-mp="15g6iv08f6ilx"></div>

          <div className="text-center mb-10" data-id="element-108" data-mp="15i9hg8nn1k29">
            <h2 className="text-3xl md:text-4xl font-bold text-espresso mb-4" data-id="element-109" data-mp="19gqgs60thq94">
              ¿Tiene un proyecto en desarrollo?
            </h2>
            <p className="text-espresso/70 text-lg font-light" data-id="element-110" data-mp="1x95ub99p7opj">
              Solicite hoy una asesoría técnica para presupuestos corporativos o
              residenciales de alta gama.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" noValidate data-id="element-111" data-mp="1sgcqcnm20kei">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-112" data-mp="1sejemtwl39zn">
              <div data-id="element-113" data-mp="2g4vviu9t4h2d">
                <label htmlFor="nombre" className="block text-sm font-medium text-espresso mb-2" data-id="element-114" data-mp="2g4nk76tc1jta">
                  Nombre Completo
                </label>
                <input type="text" id="nombre" name="nombre" required disabled={isSending} value={formData.nombre} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors bg-bone/50 disabled:opacity-60" placeholder="Ej. Juan Pérez" data-id="element-115" data-mp="x693d46gsekm" />
              </div>
              <div data-id="element-116" data-mp="168s51kyikl5a">
                <label htmlFor="empresa" className="block text-sm font-medium text-espresso mb-2" data-id="element-117" data-mp="1688mq34ybuqq">
                  Empresa / Proyecto
                </label>
                <input type="text" id="empresa" name="empresa" required disabled={isSending} value={formData.empresa} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors bg-bone/50 disabled:opacity-60" placeholder="Nombre de su constructora o proyecto" data-id="element-118" data-mp="1yqctebtib3ij" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-119" data-mp="3vkgtivl6sjy5">
              <div data-id="element-120" data-mp="3vi4828g7zrgc">
                <label htmlFor="ciudad" className="block text-sm font-medium text-espresso mb-2" data-id="element-121" data-mp="4dnqrnaqwcri">
                  Ciudad
                </label>
                <select id="ciudad" name="ciudad" required disabled={isSending} value={formData.ciudad} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors bg-bone/50 appearance-none disabled:opacity-60" data-id="element-122" data-mp="d5zgf6y1bw6f">
                  <option value="" disabled data-id="element-123" data-mp="2ea1m4o31amco">
                    Seleccione una ciudad
                  </option>
                  {cities.map(city => <option key={city} value={city} data-id="element-124" data-mp="2o64nvstz4iz3">
                      {city}
                    </option>)}
                </select>
              </div>
              <div data-id="element-125" data-mp="31zvzd6r9mcxg">
                <label htmlFor="whatsapp" className="block text-sm font-medium text-espresso mb-2" data-id="element-126" data-mp="2xwpsbwwbwhpx">
                  WhatsApp
                </label>
                <input type="tel" id="whatsapp" name="whatsapp" required disabled={isSending} value={formData.whatsapp} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors bg-bone/50 disabled:opacity-60" placeholder="+57 300 000 0000" data-id="element-127" data-mp="36wmenrv8x4ht" />
              </div>
            </div>

            {/* Feedback message */}
            {feedback && <div role="status" aria-live="polite" className={`flex items-start gap-3 px-4 py-3 rounded-sm text-sm ${status === 'success' ? 'bg-gold/10 text-espresso border border-gold/40' : 'bg-red-50 text-red-800 border border-red-200'}`} data-id="element-128" data-mp="3jeoq8hdl97rq">
                {status === 'success' ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-gold" data-id="element-129" data-mp="30h4co6g9xvmf" /> : <AlertCircle size={18} className="mt-0.5 shrink-0" data-id="element-130" data-mp="tr7j398bvxub" />}
                <span data-id="element-131" data-mp="yozdc9wg3w1o">{feedback}</span>
              </div>}

            <div className="pt-4 text-center" data-id="element-132" data-mp="3iltgh7t7jtzi">
              <button type="submit" disabled={isSending} className="inline-flex items-center justify-center bg-gold hover:bg-gold-light text-espresso font-semibold px-8 py-4 text-lg transition-all duration-300 w-full md:w-auto rounded-sm disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-133" data-mp="3inmj8c0p9u28">
                {isSending ? <>
                    Enviando...
                    <Loader2 className="ml-2 w-5 h-5 animate-spin" data-id="element-134" data-mp="2uvfrxr9u0d9o" />
                  </> : <>
                    Solicitar Asesoría
                    <Send className="ml-2 w-5 h-5" data-id="element-135" data-mp="2kxrnuaqc5teu" />
                  </>}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>;
}